import React, { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { customersApi, usersApi, sitesApi, devicesApi, steeringPreferencesApi, marketContextsApi, electricalSystemsApi } from "../api/entitiesApi";
import { sendConfigApi, joinCustomerApi, customerDetailsApi } from "../api/actionsApi";
import { metricsApi, solarApi, deviceHealthApi, alertsApi } from "../api/energyDataApi";
import { edgeVersionsApi } from "../api/entitiesApi";
import { updateDevicesApi } from "../api/actionsApi";
import { parseLatestMetrics } from "../utils/metricsParsers";
import { Loading, WarningAlert, ExpandableItem, InfoCard, SimpleFormModal, ConfirmationModal, NotificationToast, InformationalModal, SelectableListModal } from "../components";
import { useEntity } from "../hooks/useEntity";
import { Button, UncontrolledTooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    customerFields, customerDefaultValues, userFields, userDefaultValues, siteFields, siteDefaultValues, deviceFields, deviceDefaultValues,
    steeringPreferenceFields, steeringPreferenceDefaultValues, marketContextFields, marketContextDefaultValues, electricalSystemFields, electricalSystemDefaultValues
} from "../utils/entityFormFields";
import { useAuth0 } from "@auth0/auth0-react";
import { handleSiteSteeringFieldChange } from "../utils/formFieldCouplingHandler";

const BlueTitle = { color: "#3F75A2" };

const apis = {
    customer: customersApi,
    user: usersApi,
    site: sitesApi,
    device: devicesApi,
    steering_preference: steeringPreferencesApi,
    market_context: marketContextsApi,
    electrical_system: electricalSystemsApi,
};

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

function CustomerDetails() {
    const { customerId } = useParams();
    const { user: auth0User } = useAuth0();

    const api = useMemo(() => ({
        getData: () => customerDetailsApi.get(customerId),
    }), [customerId]);

    const { data: details, isLoading, error, setData } = useEntity(api, null);

    const [currentForm, setCurrentForm] = useState(null);
    const [deleteData, setDeleteData] = useState(null);
    const [notification, setNotification] = useState({ visible: false, title: "", message: "" });
    const [errorModal, setErrorModal] = useState({ visible: false, title: "", message: "" });

    const { customer, users, devices, sites, steering_preferences, market_contexts, electrical_systems } = details || {};
    const [steeringPrefs, setSteeringPrefs] = useState([]);
    const [deviceType, setDeviceType] = useState(
        currentForm?.data?.device_type ?? deviceDefaultValues.device_type
    );

    const [joinEnabled, setJoinEnabled] = useState(true);
    useEffect(() => {
        if (auth0User && users) {
            setJoinEnabled(!users.some(u => u.idp_id === auth0User.sub));
        }
    }, [auth0User, users]);

    const [siteMetrics, setSiteMetrics] = useState({});
    const [siteSolarForecasts, setSiteSolarForecasts] = useState({});
    const [siteDeviceHealthCheck, setSiteDeviceHealthCheck] = useState({});
    const [siteAlerts, setSiteAlerts] = useState({});

    const [errorMessage, setErrorMessage] = useState({
        open: false,
        title: "",
        message: "",
    });
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [availableVersions, setAvailableVersions] = useState([]);
    const [pendingUpdateSite, setPendingUpdateSite] = useState(null);
    const [pendingUpdateVersion, setPendingUpdateVersion] = useState(null);
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
    function resetUpdateState() {
        setShowUpdateConfirm(false);
        setPendingUpdateVersion(null);
        setPendingUpdateSite(null);
    }
    function showNotification(title, message) {
        setNotification({ visible: true, title, message });
        setTimeout(() => setNotification({ visible: false, title: "", message: "" }), 2000);
    }
    function showError(title, message) {
        setErrorMessage({ open: true, title, message });
    }

    async function handleUpdateClick(site, e) {
        e?.stopPropagation?.();
        try {
            const response = await edgeVersionsApi.getData();
            if (!response || response.length === 0) {
                throw new Error();
            }
            response.sort(
                (a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at)
            );
            setAvailableVersions(response);
            setPendingUpdateSite(site);
            setIsUpdateModalOpen(true);
        } catch (err) {
            showError("Error", "Failed to load versions.");
        }
    }

    useEffect(() => {
        if (!isLoading && details && Array.isArray(sites)) {
            sites.forEach((site) => {
                if (siteMetrics[site.id]) return;

                const end = new Date().toISOString();
                const start = new Date(Date.now() - 1 * 3600_000).toISOString();

                metricsApi
                    .getAllMetrics(site.id, start, end, 100)
                    .then((result) => {
                        result = Array.isArray(result) ? parseLatestMetrics(result, site.id) : {};
                        setSiteMetrics((prev) => ({ ...prev, [site.id]: result }));
                    })
                    .catch((err) => {
                        console.error(`failed to fetch metrics for site ${site.id}`, err);
                    });
            });
        }
    }, [isLoading, details, sites, siteMetrics]);

    useEffect(() => {
        if (!isLoading && details && Array.isArray(sites) && Array.isArray(electrical_systems)) {
            sites.forEach((site) => {
                const siteHasPv = electrical_systems.some(
                    (es) => es.site_id === site.id && es.has_pv
                );

                if (!siteHasPv) return;
                if (siteSolarForecasts[site.id]) return;

                const end = new Date().toISOString();
                const start = new Date(Date.now() - 1 * 3600_000).toISOString();

                solarApi
                    .getData(site.id, start, end)
                    .then((result) => {
                        result = Array.isArray(result) ? result : {};
                        setSiteSolarForecasts((prev) => ({ ...prev, [site.id]: result }));
                    })
                    .catch((err) => {
                        console.error(`failed to fetch solar forecast for site ${site.id}`, err);
                    });
            });
        }
    }, [isLoading, details, sites, electrical_systems, siteSolarForecasts]);

    useEffect(() => {
        if (!isLoading && details && Array.isArray(sites)) {
            sites.forEach((site) => {

                if (siteDeviceHealthCheck[site.id]) return;
                deviceHealthApi
                    .getData(site.id)
                    .then((result) => {
                        result = Array.isArray(result) ? result : {};
                        setSiteDeviceHealthCheck((prev) => ({ ...prev, [site.id]: result }));
                    })
                    .catch((err) => {
                        console.error(`failed to fetch Health check for site ${site.id}`, err);
                    });
            });
        }
    }, [isLoading, details, sites, siteDeviceHealthCheck]);

    useEffect(() => {
        if (!isLoading && details && Array.isArray(sites)) {
            sites.forEach((site) => {
            if (siteAlerts[site.id]) return;
            alertsApi
                .getData(site.id, 10)
                .then((result) => {
                    const data = Array.isArray(result) ? result : [];
                    setSiteAlerts((prev) => ({ ...prev, [site.id]: data }));
                })
                .catch((err) => {
                    setSiteAlerts((prev) => ({ ...prev, [site.id]: [] }));
                });
            });
        }
    }, [isLoading, details, sites, siteAlerts]);

    async function confirmSiteUpdate(site, version) {
        try {
          await updateDevicesApi.post(version.id, site.id);
          showNotification("Site Updated", `Site "${site.id}" updated to version ${version.version}.`);
        } catch {
          showError("Update Failed", `Something went wrong while trying to update site ${site.id}.`);
        }
      }

    if (isLoading) {
        return <Loading />;
    }

    if (error || details === null) {
        const displayError = error
            ? error.split("\n").map((line, index) => (
                <React.Fragment key={index}>
                    {line}
                    <br />
                </React.Fragment>
            ))
            : "Ooops! Something went wrong...";
        return (
            <div className="d-flex flex-column align-items-center">
                <WarningAlert message={displayError} />
            </div>
        );
    }

    async function handleSendConfig(site_id) {
        try {
            await sendConfigApi.post(site_id);
            const refreshedData = await customerDetailsApi.get(customerId);
            setData(refreshedData);

            setNotification({
                visible: true,
                title: "Send Config Successful",
                message: "You have successfully sent the config update.",
            });
        } catch (err) {
            setErrorModal({
                visible: true,
                title: "Error Sending Config",
                message: err.message,
            });
        } finally {
            setTimeout(() => setNotification({ visible: false, title: "", message: "" }), 2000);
        }
    }

    async function handleJoin() {
        try {
            await joinCustomerApi.patch(auth0User.sub, { customer_id: customer.id });
            const refreshedData = await customerDetailsApi.get(customerId);
            setData(refreshedData);

            setNotification({
                visible: true,
                title: "Join Successful",
                message: "You have successfully joined this customer.",
            });
        } catch (err) {
            setErrorModal({
                visible: true,
                title: "Error Joining Customer",
                message: err.message,
            });
        } finally {
            setTimeout(() => setNotification({ visible: false, title: "", message: "" }), 2000);
        }
    }

    async function handleSaveForm(formData) {
        const entity = currentForm.entity;
        try {
            if (currentForm.mode === "edit") {
                await apis[entity].updateEntity(formData.id, formData);
                const refreshedData = await customerDetailsApi.get(customerId);
                setData(refreshedData);

                setNotification({
                    visible: true,
                    title: capitalize(entity) + " Edited",
                    message: `1 ${entity} has been edited`,
                });
            } else {
                await apis[entity].addEntity(formData);
                const refreshedData = await customerDetailsApi.get(customerId);
                setData(refreshedData);

                setNotification({
                    visible: true,
                    title: capitalize(entity) + " Added",
                    message: `1 ${entity} has been added`,
                });
            }
        } catch (err) {
            setErrorModal({
                visible: true,
                title: "Error " + (currentForm.mode === "edit" ? "Updating " : "Adding ") + capitalize(entity),
                message: err.message,
            });
        } finally {
            setCurrentForm(null);
            setTimeout(() => setNotification({ visible: false, title: "", message: "" }), 2000);
        }
    }

    async function confirmDelete() {
        const entity = deleteData.entity;
        try {
            await apis[entity].deleteEntity(deleteData.data.id);
            const refreshedData = await customerDetailsApi.get(customerId);
            setData(refreshedData);

            setNotification({
                visible: true,
                title: capitalize(entity) + " Deleted",
                message: `1 ${entity} has been deleted`,
            });
        } catch (err) {
            setErrorModal({
                visible: true,
                title: "Error Deleting " + capitalize(entity),
                message: err.message,
            });
        } finally {
            setDeleteData(null);
            setTimeout(() => setNotification({ visible: false, title: "", message: "" }), 2000);
        }
    }

    function renderEntityActions(entityType, entityData, preEdit = null) {
        const idPrefix = `${entityType}-${entityData.id}`;
        return (
            <>  {entityType === "site" && (
                <>
                    <Button
                        id={`${idPrefix}-send-config`}
                        aria-label="Send config"
                        color="link"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSendConfig(entityData.id);
                        }}
                    >
                        <FontAwesomeIcon icon="wrench" style={{ fontSize: "1.2em", color: "#6c757d" }}/>
                    </Button>
                    <UncontrolledTooltip
                        target={`${idPrefix}-send-config`}
                        placement="top"
                    >
                        Send config to edge
                    </UncontrolledTooltip>
                </>
            )}
                <Button
                    id={`${idPrefix}-edit`}
                    aria-label="Edit"
                    color="link"
                    size="sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (typeof preEdit === "function") preEdit(entityData);
                        setCurrentForm({ entity: entityType, data: entityData, mode: "edit" });
                    }}
                >
                    <FontAwesomeIcon icon="pencil-alt" style={{ fontSize: "1.2em" }} />
                </Button>
                <UncontrolledTooltip target={`${idPrefix}-edit`} placement="top">
                    Edit
                </UncontrolledTooltip>
                {entityType === "site" && (
                    <>
                        <Button
                        id={`${idPrefix}-update`}
                        size="sm"
                        color="link"
                        title="Update site"
                        aria-label="Update Site"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateClick(entityData);
                        }}
                        >
                        <FontAwesomeIcon icon="code-compare" style={{ fontSize: "1.2em", color: "#17a2b8" }} />
                        </Button>
                        <UncontrolledTooltip target={`${idPrefix}-update`} placement="top">
                        Update edge version
                        </UncontrolledTooltip>
                    </>
                )}
                <Button
                    id={`${idPrefix}-delete`}
                    aria-label="Delete"
                    color="link"
                    size="sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        setDeleteData({ entity: entityType, data: entityData });
                    }}
                >
                    <FontAwesomeIcon icon="trash" style={{ fontSize: "1.2em", color: "#fc6a6d" }} />
                </Button>
                <UncontrolledTooltip target={`${idPrefix}-delete`} placement="top">
                    Delete
                </UncontrolledTooltip>
            </>
        );
    }

    return (
        <>

            <div style={{ padding: "16px" }}>
                <ExpandableItem
                    title={customer.display_name}
                    defaultExpanded={true}
                    headerTitleStyle={BlueTitle}
                    card={<InfoCard data={customer} fields={["Id", ...customerFields()]} entity={"customer"} />}
                    actions={
                        <>
                            <Button
                                aria-label="Odoo"
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`https://revolta.odoo.com/odoo/contacts/${customer.customer_reference}`, "_blank")
                                }}
                                className="m-1"
                            >
                                Odoo
                            </Button>
                            <Button
                                aria-label="Join"
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleJoin();
                                }}
                                className={joinEnabled ? "join-active" : "join-disabled"}
                                disabled={!joinEnabled}
                            >
                                Join
                            </Button>

                            <Button
                                aria-label="Edit"
                                color="link"
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentForm({ entity: "customer", data: customer, mode: "edit" });
                                }}
                            >
                                <FontAwesomeIcon icon="pencil-alt" style={{ fontSize: "1.2em" }} />
                            </Button>
                        </>
                    }
                >

                    <ExpandableItem
                        title={`Users (${users.length})`}
                        headerTitleStyle={BlueTitle}
                        actions={
                            <Button
                                color="link"
                                size="sm"
                                aria-label="Add User"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentForm({ entity: "user", data: { customer_id: customer.id }, mode: "add" });
                                }}
                            >
                                <FontAwesomeIcon icon="plus" style={{ fontSize: "1.2em" }} />
                            </Button>
                        }
                    >
                        {users.slice().sort((a, b) => a.id.localeCompare(b.id)).map((u) => (
                            <ExpandableItem
                                key={u.id}
                                title={`User: ${u.id}`}
                                card={<InfoCard data={u} fields={["Id", ...userFields()]} entity={"user"} />}
                                actions={renderEntityActions("user", u)}
                            />
                        ))}
                    </ExpandableItem>

                    <ExpandableItem
                        title={`Sites (${sites.length})`}
                        headerTitleStyle={BlueTitle}
                        actions={
                            <Button
                                color="link"
                                size="sm"
                                aria-label="Add Site"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentForm({ entity: "site", data: { customer_id: customer.id }, mode: "add" });
                                }}
                            >
                                <FontAwesomeIcon icon="plus" style={{ fontSize: "1.2em" }} />
                            </Button>
                        }
                    >
                        {sites.slice().sort((a, b) => a.id.localeCompare(b.id)).map((site) => {
                            const siteDevices = devices.filter(d => d.site_id === site.id);
                            const siteSteeringPrefs = steering_preferences.filter(sp => sp.site_id === site.id);
                            const siteMarketContexts = market_contexts.filter(mc => mc.site_id === site.id);
                            const siteElectricalSystems = electrical_systems.filter(es => es.site_id === site.id);

                            return (
                                <ExpandableItem
                                    key={site.id}
                                    title={`Site: ${site.id}`}
                                    card={<InfoCard data={site} fields={["Id", ...siteFields(), "Active_steering_mode", "Preferred_steering_mode"]} entity={"site"} metrics={siteMetrics[site.id]} solarForecasts={siteSolarForecasts[site.id]}  deviceHealthCheck={siteDeviceHealthCheck[site.id]} siteAlerts={siteAlerts[site.id]}/>}
                                    actions={renderEntityActions("site", site, () => setSteeringPrefs(siteSteeringPrefs))}
                                >
                                    <ExpandableItem
                                        title={`Devices (${siteDevices.length})`}
                                        headerTitleStyle={BlueTitle}
                                        actions={
                                            <Button
                                                color="link"
                                                size="sm"
                                                aria-label="Add Device"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCurrentForm({ entity: "device", data: { site_id: site.id, customer_id: customer.id }, mode: "add" });
                                                }}
                                            >
                                                <FontAwesomeIcon icon="plus" style={{ fontSize: "1.2em" }} />
                                            </Button>
                                        }
                                    >
                                        {siteDevices.slice().sort((a, b) => a.id.localeCompare(b.id)).map((device) => (
                                            <ExpandableItem
                                                key={device.id}
                                                title={`Device: ${device.id}`}
                                                card={<InfoCard data={device}
                                                                fields={["Id", ...deviceFields(device.device_type)]} entity={"device"} />}
                                                                actions={renderEntityActions("device", device, (d) => {
                                                                    setDeviceType(d?.device_type ?? deviceDefaultValues.device_type);
                                                                })}
                                            />
                                        ))}
                                    </ExpandableItem>


                                    <ExpandableItem
                                        title={`Steering_preferences (${siteSteeringPrefs.length})`}
                                        headerTitleStyle={BlueTitle}
                                        actions={
                                            <Button
                                                color="link"
                                                size="sm"
                                                aria-label="Add Steering_Preference"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCurrentForm({ entity: "steering_preference", data: { site_id: site.id, customer_id: customer.id }, mode: "add" });
                                                }}
                                            >
                                                <FontAwesomeIcon icon="plus" style={{ fontSize: "1.2em" }} />
                                            </Button>
                                        }
                                    >
                                        {siteSteeringPrefs.slice().sort((a, b) => a.id.localeCompare(b.id)).map((sp) => {
                                            const isActive = sp.id === site.active_steering_mode;
                                            return (
                                                <ExpandableItem
                                                    key={sp.id}
                                                    title={`Steering_preference: ${sp.id}`}
                                                    card={<InfoCard data={sp} fields={["Id", ...steeringPreferenceFields()]} entity={"steering_preference"} />}
                                                    headerStyle={isActive ? { backgroundColor: "#d4edda" } : {}}
                                                    actions={renderEntityActions("steering_preference", sp)}
                                                />
                                            );
                                        })}
                                    </ExpandableItem>


                                    <ExpandableItem
                                        title={`Market_contexts (${siteMarketContexts.length})`}
                                        headerTitleStyle={BlueTitle}
                                        actions={
                                            siteMarketContexts.length === 0 ?
                                                <Button
                                                    color="link"
                                                    size="sm"
                                                    aria-label="Add Market_Context"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setCurrentForm({ entity: "market_context", data: { site_id: site.id, customer_id: customer.id }, mode: "add" });
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon="plus" style={{ fontSize: "1.2em" }} />
                                                </Button>
                                                : ""
                                        }
                                    >
                                        {siteMarketContexts.map((mc) => (
                                            <ExpandableItem
                                                key={mc.id}
                                                title={`Market_context: ${mc.id}`}
                                                card={<InfoCard data={mc} fields={["Id", ...marketContextFields()]} entity={"market_context"} />}
                                                actions={renderEntityActions("market_context", mc)}
                                            />
                                        ))}
                                    </ExpandableItem>


                                    <ExpandableItem
                                        title={`Electrical_systems (${siteElectricalSystems.length})`}
                                        headerTitleStyle={BlueTitle}
                                        actions={
                                            siteElectricalSystems.length === 0 ?
                                                <Button
                                                    color="link"
                                                    size="sm"
                                                    aria-label="Add Electrical_System"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setCurrentForm({ entity: "electrical_system", data: { site_id: site.id, customer_id: customer.id }, mode: "add" });
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon="plus" style={{ fontSize: "1.2em" }} />
                                                </Button>
                                                : ""
                                        }
                                    >
                                        {siteElectricalSystems.map((es) => (
                                            <ExpandableItem
                                                key={es.id}
                                                title={`Electrical_system: ${es.id}`}
                                                card={<InfoCard data={es} fields={["Id", ...electricalSystemFields()]} entity={"electrical_system"} />}
                                                actions={renderEntityActions("electrical_system", es)}
                                            />
                                        ))}
                                    </ExpandableItem>

                                </ExpandableItem>
                            );
                        })}
                    </ExpandableItem>
                </ExpandableItem>
            </div>

            {currentForm && (
                <SimpleFormModal
                    isOpen={true}
                    title={`${currentForm.mode === "edit" ? "Edit" : "Add"} ${currentForm.entity}`}
                    initialData={currentForm.data}
                    fields={
                        currentForm.entity === "customer"
                            ? customerFields()
                            : currentForm.entity === "user"
                                ? userFields()
                                : currentForm.entity === "site"
                                    ? currentForm.mode === "edit"
                                        ? [
                                            ...siteFields(),
                                            {
                                                name: "active_steering_mode",
                                                label: "ActiveSteeringMode",
                                                type: "select",
                                                options: [
                                                ...steeringPrefs.map(p => ({ value: p.id, label: p.preference_type })),
                                                ],
                                            },
                                            {
                                                name: "preferred_steering_mode",
                                                label: "PreferredSteeringMode",
                                                type: "select",
                                                options: steeringPrefs
                                                .filter(p => p.preference_type !== "Idle")
                                                .map(p => ({ value: p.id, label: p.preference_type })),
                                            },
                                        ]
                                        : siteFields()
                                    : currentForm.entity === "device"
                                        ? deviceFields(deviceType)
                                        : currentForm.entity === "steering_preference"
                                            ? steeringPreferenceFields()
                                            : currentForm.entity === "market_context"
                                                ? marketContextFields()
                                                : electricalSystemFields()
                    }
                    onSave={handleSaveForm}
                    onCancel={() => setCurrentForm(null)}
                    defaultValues={
                        currentForm.entity === "customer"
                            ? customerDefaultValues
                            : currentForm.entity === "user"
                                ? userDefaultValues
                                : currentForm.entity === "site"
                                    ? siteDefaultValues
                                    : currentForm.entity === "device"
                                        ? deviceDefaultValues
                                        : currentForm.entity === "steering_preference"
                                            ? steeringPreferenceDefaultValues
                                            : currentForm.entity === "market_context"
                                                ? marketContextDefaultValues
                                                : electricalSystemDefaultValues
                    }
                    onFieldChange={
                        currentForm.entity === "site"
                        ? handleSiteSteeringFieldChange(steeringPrefs)
                        : currentForm.entity === "device"
                        ? (name, value) => { if (name === "device_type") setDeviceType(value); }
                        : undefined
                    }
                />
            )}

        <ConfirmationModal
                isOpen={showUpdateConfirm}
                title="Confirm Update"
                message={pendingUpdateSite && pendingUpdateVersion ? `Update site "${pendingUpdateSite.id}" to version "${pendingUpdateVersion.version}"?` : ""}
                onConfirm={() => { confirmSiteUpdate(pendingUpdateSite, pendingUpdateVersion); resetUpdateState(); }}
                onCancel={resetUpdateState}
                confirmLabel="Yes, Update"
                cancelLabel="Cancel"
            />


        <SelectableListModal
            isOpen={isUpdateModalOpen}
            title="Update Site Version"
            items={availableVersions}
            helpMessage="* This will update the selected site to the chosen version."
            getItemId={(v) => v.id}
            renderItem={(v) => (
            <>
                <div className="fw-semibold">{v.version}</div>
                <div className="small text-muted">Uploaded: {new Date(v.uploaded_at).toLocaleString()}</div>
            </>
            )}
            onCancel={() => setIsUpdateModalOpen(false)}
            onConfirm={(selectedItem) => {
            setPendingUpdateVersion(selectedItem);
            setIsUpdateModalOpen(false);
            setShowUpdateConfirm(true);
            }}
        />

        {deleteData && (
            <ConfirmationModal
            isOpen={true}
            title={`Delete ${deleteData.entity}?`}
            message={`Are you sure you want to delete this ${deleteData.entity}?`}
            onConfirm={confirmDelete}
            onCancel={() => setDeleteData(null)}
            confirmLabel="OK"
            cancelLabel="Cancel"
            />
        )}

        {errorModal.visible && (
            <InformationalModal
            isOpen={true}
            title={errorModal.title}
            message={errorModal.message}
            onCancel={() =>
                setErrorModal({ visible: false, title: "", message: "" })
            }
            />
        )}

        {errorMessage.open && (
            <InformationalModal
            isOpen={errorMessage.open}
            title={errorMessage.title}
            message={errorMessage.message}
            onCancel={() =>
                setErrorMessage({ open: false, title: "", message: "" })
            }
            />
        )}

        {notification.visible && (
            <NotificationToast
            icon="exclamation-triangle"
            title={notification.title}
            message={notification.message}
            fade={true}
            />
        )}
        </>
    );
}

export default CustomerDetails;
